import React, { useState, useMemo } from "react";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { scoreTeam, teamVerdict, scoreColor } from "../services/compatibility";
import { getArchetype, classifyEngine } from "../data/archetypes";
import "../styles.css";

function getInitials(name) { return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(); }
function getRoleClass(role) {
  const r = (role || "").toLowerCase();
  if (r.includes("supt")) return "supt";
  if (r.includes("pm")) return "pm";
  if (r.includes("apm")) return "apm";
  if (r.includes("fe")) return "fe";
  return "other";
}

function MiniRing({ score, size = 64 }) {
  const deg = (score / 100) * 360;
  const color = score >= 80 ? "#3db87a" : score >= 65 ? "#dfab01" : score >= 50 ? "#e8a04a" : "#e08a8a";
  const col = scoreColor(score);
  const inner = size - 14;
  return (
    <div className="score-ring" style={{ width: size, height: size, background: `conic-gradient(${color} 0deg ${deg}deg, var(--bg-elevated) ${deg}deg 360deg)` }}>
      <div className="score-ring-inner" style={{ width: inner, height: inner }}>
        <div className="score-ring-num" style={{ color: col.text, fontSize: size > 70 ? 24 : 20 }}>{score}</div>
      </div>
    </div>
  );
}

function CoverageDots({ coverage }) {
  const items = [
    { key: "driver", label: "Driver" },
    { key: "anchor", label: "Anchor" },
    { key: "quality", label: "Quality" },
    { key: "glue", label: "Glue" },
  ];
  return (
    <div className="cov-dots">
      {items.map(it => (
        <div key={it.key} className={`cov-dot ${coverage[it.key] ? "on" : "off"}`}>
          <Icon name={coverage[it.key] ? "circle-check" : "x-circle"} size={14} />
          {it.label}
        </div>
      ))}
    </div>
  );
}

function TeamColumn({ title, people, swapName, highlightName, accent }) {
  const ts = scoreTeam(people);
  const engine = { gas: 0, brake: 0, glue: 0 };
  people.forEach(p => engine[classifyEngine(p)]++);

  return (
    <div className="swap-col" style={accent ? { borderTopColor: "var(--gold)" } : {}}>
      <div className="swap-col-head">
        <div className="swap-col-title">{title}</div>
        {ts && <MiniRing score={ts.score} />}
      </div>
      {ts && <div className="swap-verdict">{teamVerdict(ts.score)}</div>}

      <div className="swap-engine">
        <span className="swap-eng-item"><b style={{ color: "var(--gold)" }}>{engine.gas}</b> Gas</span>
        <span className="swap-eng-item"><b style={{ color: "var(--blue)" }}>{engine.brake}</b> Brake</span>
        <span className="swap-eng-item"><b style={{ color: "var(--green-bar)" }}>{engine.glue}</b> Glue</span>
      </div>

      {ts && <CoverageDots coverage={ts.coverage} />}

      <div className="swap-roster">
        {people.map((p, i) => {
          const arch = getArchetype(p.Archetype);
          const isOut = p.FullName === swapName;
          const isIn = p.FullName === highlightName;
          return (
            <div key={i} className={`swap-member ${isOut ? "out" : ""} ${isIn ? "in" : ""}`}>
              <div className={`avatar ${getRoleClass(p.CompanyRole)}`} style={{ width: 30, height: 30, fontSize: 10 }}>{getInitials(p.FullName)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="swap-member-name">{p.FullName}</div>
                <div className="swap-member-arch"><Icon name={arch.icon} size={11} /> {p.Archetype}</div>
              </div>
              {isOut && <span className="swap-tag out">OUT</span>}
              {isIn && <span className="swap-tag in">IN</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SwapCompare() {
  const { people: peopleRaw, projects, teamMembers, loading, error } = useData();
  const allPeople = (peopleRaw || []).filter(p => p.IsActive !== false && p.Archetype);

  // Only projects that have at least 2 profiled members make sense here
  const eligibleProjects = (projects || []).filter(proj => {
    const members = (teamMembers || []).filter(m => m.ProjectJobNumber === proj.Title);
    const profiled = members.filter(m => allPeople.find(p => p.FullName === m.PersonName));
    return profiled.length >= 2;
  }).sort((a, b) => (a.Title || "").localeCompare(b.Title || ""));

  const [jobNumber, setJobNumber] = useState("");
  const [outName, setOutName] = useState("");
  const [inId, setInId] = useState("");

  const activeJob = jobNumber || eligibleProjects[0]?.Title || "";
  const members = (teamMembers || []).filter(m => m.ProjectJobNumber === activeJob);
  const currentPeople = members
    .map(m => allPeople.find(p => p.FullName === m.PersonName))
    .filter(Boolean);

  // The person being swapped out, and their role on this project
  const outMember = members.find(m => m.PersonName === outName);
  const replacement = allPeople.find(p => String(p.Id) === String(inId));

  // Candidate pool for replacement = profiled people NOT already on this team
  const onTeam = new Set(currentPeople.map(p => p.FullName));
  const candidatePool = allPeople.filter(p => !onTeam.has(p.FullName))
    .sort((a, b) => (a.FullName || "").localeCompare(b.FullName || ""));
  const currentSorted = [...currentPeople].sort((a, b) => (a.FullName || "").localeCompare(b.FullName || ""));

  // Build the proposed team
  const proposedPeople = useMemo(() => {
    if (!outName || !replacement) return currentPeople;
    return currentPeople.map(p => (p.FullName === outName ? replacement : p));
  }, [currentPeople, outName, replacement]);

  const currentScore = scoreTeam(currentPeople);
  const proposedScore = scoreTeam(proposedPeople);
  const hasSwap = outName && replacement;
  const delta = hasSwap && currentScore && proposedScore ? proposedScore.score - currentScore.score : 0;

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading teams…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Swap &amp; Compare</div>
          <div className="page-subtitle">Replace a team member and see how the dynamics shift</div>
        </div>
      </div>

      {/* Controls */}
      <div className="card mb-24">
        <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px" }}>
            <label className="form-label">Project Team</label>
            <select className="form-select" value={activeJob} onChange={e => { setJobNumber(e.target.value); setOutName(""); setInId(""); }}>
              {eligibleProjects.map(p => <option key={p.Title} value={p.Title}>{p.Title} — {p.JobName}</option>)}
            </select>
          </div>
          <div style={{ flex: "1 1 180px" }}>
            <label className="form-label">Swap Out</label>
            <select className="form-select" value={outName} onChange={e => setOutName(e.target.value)}>
              <option value="">Select member...</option>
              {currentSorted.map(p => <option key={p.Id} value={p.FullName}>{p.FullName} ({members.find(m => m.PersonName === p.FullName)?.ProjectRole})</option>)}
            </select>
          </div>
          <div style={{ fontSize: "20px", color: "var(--text-muted)", paddingBottom: "8px" }}>
            <Icon name="arrow-swap" size={20} />
          </div>
          <div style={{ flex: "1 1 180px" }}>
            <label className="form-label">Swap In</label>
            <select className="form-select" value={inId} onChange={e => setInId(e.target.value)} disabled={!outName}>
              <option value="">Select replacement...</option>
              {candidatePool.map(p => <option key={p.Id} value={p.Id}>{p.FullName} — {p.Archetype}</option>)}
            </select>
          </div>
        </div>
      </div>

      {eligibleProjects.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="arrow-swap" size={34} /></div>
          <div className="empty-text">No projects have enough profiled members yet.<br />Add more Culture Index profiles to use Swap &amp; Compare.</div>
        </div>
      ) : (
        <>
          {/* Delta banner */}
          {hasSwap && (
            <div className={`swap-delta ${delta > 0 ? "up" : delta < 0 ? "down" : "flat"}`}>
              <Icon name={delta > 0 ? "up" : delta < 0 ? "down" : "flat"} size={22} />
              <div>
                <div className="swap-delta-num">
                  {delta > 0 ? "+" : ""}{delta} points
                </div>
                <div className="swap-delta-text">
                  {delta > 3 && "This swap meaningfully strengthens the team."}
                  {delta > 0 && delta <= 3 && "This swap slightly improves the team."}
                  {delta === 0 && "This swap keeps team compatibility about the same."}
                  {delta < 0 && delta >= -3 && "This swap slightly weakens the team."}
                  {delta < -3 && "This swap meaningfully weakens the team."}
                </div>
              </div>
              {outMember && replacement && (
                <div className="swap-delta-who">
                  <span className="swap-tag out">{outName}</span>
                  <Icon name="arrow-right" size={14} />
                  <span className="swap-tag in">{replacement.FullName}</span>
                </div>
              )}
            </div>
          )}

          {/* Side by side */}
          <div className="swap-grid">
            <TeamColumn
              title="Current Team"
              people={currentPeople}
              swapName={hasSwap ? outName : ""}
            />
            <TeamColumn
              title={hasSwap ? "Proposed Team" : "Make a swap to compare"}
              people={proposedPeople}
              highlightName={hasSwap ? replacement.FullName : ""}
              accent
            />
          </div>

          {/* What changed */}
          {hasSwap && currentScore && proposedScore && (
            <div className="card" style={{ marginTop: "18px" }}>
              <div className="card-header"><div className="card-title">What Changes</div></div>
              <CoverageDelta before={currentScore.coverage} after={proposedScore.coverage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CoverageDelta({ before, after }) {
  const items = [
    { key: "driver", label: "Driver (high autonomy)", note: "Someone to own outcomes and drive pace" },
    { key: "anchor", label: "Steady Anchor (high pace)", note: "Methodical stability for long jobs" },
    { key: "quality", label: "Quality Control (high detail)", note: "Safety, compliance, and quality focus" },
    { key: "glue", label: "Communicator (high social)", note: "Holds the team together socially" },
  ];
  return (
    <div>
      {items.map(it => {
        const b = before[it.key], a = after[it.key];
        let status, cls;
        if (b && a) { status = "Still covered"; cls = "same"; }
        else if (!b && a) { status = "Now covered"; cls = "gained"; }
        else if (b && !a) { status = "Lost"; cls = "lost"; }
        else { status = "Still missing"; cls = "missing"; }
        return (
          <div key={it.key} className="cov-delta-row">
            <Icon name={a ? "circle-check" : "x-circle"} size={18} style={{ color: a ? "var(--green)" : "var(--red)", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: "14px" }}>{it.label}</div>
              <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{it.note}</div>
            </div>
            <span className={`cov-delta-tag ${cls}`}>{status}</span>
          </div>
        );
      })}
    </div>
  );
}
